import React, { useRef, useEffect } from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
	TooltipComponent,
	GridComponent,
	LegendComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import getChartColorsArray from '../../ChartsDynamicColor';

echarts.use([
	TooltipComponent,
	GridComponent,
	LegendComponent,
	BarChart,
	CanvasRenderer,
]);

/**
 * Stacked Horizontal Bar Chart using ECharts (tree-shaking API).
 *
 * @param {Array}  series            - Array of series objects: [{ name, data: [number] }]
 * @param {Array}  yAxisCategories   - Labels for the Y axis (categories)
 * @param {string} dataColors        - JSON string of CSS var names, e.g. '["--vz-primary","--vz-success"]'
 * @param {string} height            - CSS height for the chart container (default '300px')
 * @param {object} style             - Additional inline styles for the chart container
 */
const EChartsStackedHorizontalBarChart = ({
	series,
	yAxisCategories,
	dataColors,
	colors: colorsProp,
	height = '300px',
	style,
}) => {
	const chartRef = useRef(null);

	useEffect(() => {
		let chartInstance = null;

		if (chartRef.current) {
			const colors = colorsProp ?? getChartColorsArray(dataColors);

			chartInstance = echarts.init(chartRef.current);
			chartInstance.setOption({
				textStyle: { fontSize: 11 },
				color: colors,
				tooltip: {
					trigger: 'axis',
					axisPointer: { type: 'shadow' },
					textStyle: { fontSize: 11 },
				},
				legend: {
					bottom: 0,
					type: 'scroll',
				},
				grid: {
					left: '3%',
					right: '4%',
					bottom: '15%',
					top: '3%',
					containLabel: true,
				},
				xAxis: {
					type: 'value',
					textStyle: { fontSize: 11 },
				},
				yAxis: {
					type: 'category',
					data: yAxisCategories,
					axisLabel: {
						overflow: 'truncate',
						width: 100,
						textStyle: { fontSize: 11 },
					},
				},
				series: series.map(s => ({
					name: s.name,
					type: 'bar',
					stack: 'total',
					label: {
						show: true,
						formatter: ({ value }) => (value > 0 ? value : ''),
					},
					emphasis: { focus: 'series' },
					data: s.data,
				})),
			});

			// Resize immediately after init to fill container
			setTimeout(() => {
				chartInstance?.resize();
			}, 0);
		}

		const resizeChart = () => {
			chartInstance?.resize();
		};

		window.addEventListener('resize', resizeChart);

		return () => {
			chartInstance?.dispose();
			window.removeEventListener('resize', resizeChart);
		};
	}, [series, yAxisCategories, dataColors]);

	// ResizeObserver for dynamic container size changes
	useEffect(() => {
		if (!chartRef.current) return;

		const el = chartRef.current;

		const resizeObserver = new ResizeObserver(() => {
			if (!el?.isConnected) return;
			const chart = echarts.getInstanceByDom(el);
			if (chart) chart.resize();
		});

		resizeObserver.observe(el);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	return (
		<div
			ref={chartRef}
			style={{
				width: '100%',
				height,
				...style,
			}}
		/>
	);
};

export default EChartsStackedHorizontalBarChart;
